import { getCurrentUser } from '../../../services/helper';
import { Recipe } from '../../../types/lessonPlan.type';
import { boldIngredientMentions, getIngredientLabels } from '../../../utils/recipeStepUtils';
import { parseRecipeSteps } from '../../../utils/utils';
import SafeHTMLDisplay from '../../common/SafeHTMLDisplay';
import SafeHTMLHeader from '../../common/SafeHTMLHeader';
import './RecipeSteps.css';

function toFraction(value: number): string {
    const whole = Math.floor(value);
    const decimal = value - whole;

    const fractions: { decimal: number; display: string }[] = [
        { decimal: 0, display: '' },
        { decimal: 1/8, display: '1/8' },
        { decimal: 1/4, display: '1/4' },
        { decimal: 1/3, display: '1/3' },
        { decimal: 3/8, display: '3/8' },
        { decimal: 1/2, display: '1/2' },
        { decimal: 5/8, display: '5/8' },
        { decimal: 2/3, display: '2/3' },
        { decimal: 3/4, display: '3/4' },
        { decimal: 7/8, display: '7/8' },
        { decimal: 1, display: '1' },
    ];

    const nearest = fractions.reduce((prev, curr) =>
        Math.abs(curr.decimal - decimal) < Math.abs(prev.decimal - decimal) ? curr : prev
    );

    if (nearest.decimal === 1) return (whole + 1).toString();
    if (nearest.decimal === 0) return whole === 0 ? '0' : whole.toString();
    return whole > 0 ? `${whole} ${nearest.display}` : nearest.display;
}

function scaleStepContent(content: string, scale: number): string {
    return content.replace(/\{\{([^}]+)\}\}/g, (_, num) => {
        let value: number;
        const trimmed = num.trim();
        // Mixed number: e.g. "1 1/2"
        const mixedMatch = trimmed.match(/^(\d+)\s+(\d+)\/(\d+)$/);
        if (mixedMatch) {
            value = parseInt(mixedMatch[1]) + parseInt(mixedMatch[2]) / parseInt(mixedMatch[3]);
        } else if (trimmed.includes('/')) {
            const [numerator, denominator] = trimmed.split('/').map(Number);
            value = numerator / denominator;
        } else {
            value = parseFloat(trimmed);
        }
        return toFraction(value * scale);
    });
}

interface RecipeSteps {
    recipes: Recipe[];
    scale?: number;
}

const RecipeSteps: React.FC<RecipeSteps> = ({ recipes, scale = 1 }) => {
    const currentUser = getCurrentUser();
    return (
        <>
            <div
                className="instructions"
                id="steps"
            >
                <h3 className="text-info fw-bolder mb-4">Instructions</h3>
                <div className="instructions-recipe">
                    {recipes.map((recipe, index) => {
                        const recipeSteps = currentUser?.hasDashAccess
                            ? parseRecipeSteps(recipe.recipe_steps_instructor_field)
                            : parseRecipeSteps(recipe.recipe_steps_parent_field);
                        const ingredientLabels = getIngredientLabels(recipe.ingredients ?? []);
                        const formatStepText = (text: string) =>
                            boldIngredientMentions(scaleStepContent(text, scale), ingredientLabels);
                        return (
                            <div key={`recipe-${index}`}>
                                {recipes.length > 1 && <h4 className="text-info fw-bold mb-2">{recipe.title}</h4>}
                                {recipeSteps.map((step, index) => (
                                    <div
                                        className="instruction-steps"
                                        key={`step-${index}`}
                                    >
                                        <div>
                                            <div className="text-block-steps">{index + 1}</div>
                                        </div>
                                        <div>
                                            {step.title && step.content !== '' ? (
                                                <>
                                                    <SafeHTMLHeader htmlContent={formatStepText(step.title)} />
                                                    <SafeHTMLDisplay htmlContent={formatStepText(step.content)} />
                                                </>
                                            ) : (
                                                <>
                                                    <SafeHTMLDisplay htmlContent={formatStepText(step.title)} />
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className="dots recipe"></div>
        </>
    );
};

export default RecipeSteps;